/**
 * Utility functions for safely rendering highlighted search content
 */

/**
 * Safely render highlighted HTML content from ElasticSearch
 * Only allows <mark> tags for highlighting, strips all other HTML
 *
 * @param content - The content that may contain <mark> highlighting tags
 * @returns Sanitized content safe for dangerouslySetInnerHTML
 */
export const sanitizeHighlightedContent = (
  content: string | null | undefined
): string => {
  if (!content) return '';

  // Remove all HTML tags except <mark> and </mark>
  const sanitized = content
    .replace(/<(?!\/?(mark))[^>]*>/gi, '') // Remove all tags except <mark>
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");

  return sanitized;
};

/**
 * Create props for dangerouslySetInnerHTML with highlighted content
 *
 * @param content - The content with potential <mark> tags
 * @returns Object suitable for dangerouslySetInnerHTML prop
 */
export const createHighlightProps = (content: string | null | undefined) => ({
  __html: sanitizeHighlightedContent(content),
});

/**
 * Check if content contains highlighting
 *
 * @param content - The content to check
 * @returns True if content contains <mark> tags
 */
export const hasHighlighting = (
  content: string | null | undefined
): boolean => {
  if (!content) return false;
  return content.includes('<mark>') && content.includes('</mark>');
};

/**
 * Remove highlighting tags and return plain text
 *
 * @param content - The content with potential <mark> tags
 * @returns Plain text without highlighting
 */
export const stripHighlighting = (
  content: string | null | undefined
): string => {
  if (!content) return '';
  return content.replace(/<\/?mark>/gi, '');
};
